import {
    FETCH_FITNESSSTATS_ACTIVITY,
    FETCH_FITNESSSTATS_ACTIVITY_SUCCESS,
    FETCH_FITNESSSTATS_ACTIVITY_ERROR,
    FETCH_FITNESSSTATS_ACTIVITY_FETCHING,
    FETCH_RECENT_FITNESSSTATS_ACTIVITY,
    FETCH_RECENT_FITNESSSTATS_ACTIVITY_SUCCESS,
    FETCH_RECENT_FITNESSSTATS_ACTIVITY_FETCHING,
    FETCH_RECENT_FITNESSSTATS_ACTIVITY_ERROR,
    FETCH_USER_AVERAGE_PACE,
    FETCH_USER_AVERAGE_PACE_SUCCESS,
    FETCH_USER_AVERAGE_PACE_FETCHING,
    FETCH_USER_AVERAGE_PACE_ERROR,
    FETCH_ALL_FITNESSSTATS_ACTIVITY,
    FETCH_ALL_FITNESSSTATS_ACTIVITY_SUCCESS,
    FETCH_ALL_FITNESSSTATS_ACTIVITY_FETCHING,
    FETCH_ALL_FITNESSSTATS_ACTIVITY_ERROR
} from '../../actions/statsActions';

const INITIAL_STATE = {
    fitnessStatsFetching: false,
    fitnessStatsFetchSuccess: false,
    fitnessStatsFetchError: false,
    fitnessStatsFetchingPendingRequests: [],
    fitnessRecentStats: {
        metrics: {},
        fitnessRecentStatsFetching: {},
        fitnessRecentStatsFetchError: {},
        fitnessRecentStatsFetchSuccess: {}
    },
    userAveragePace: {
        userAvgPace: {},
        userAvgPaceFetching: false,
        userAvgPaceFetchSuccess: false,
        userAvgPaceFetchError: false
    },
    /**
     * example object, each call added to array by the start/end dates
        [
            {
                startDate: '2024-03-01',
                endDate: '2024-03-08',
                key: 'example', // use to separate data if needed
                data: [],
                allStatsFetching: false,
                allStatsError: false,
                allStatsSuccess: false
            }
        ]
     */
    fitnessAllStats: []
};

// Request
export function fetchFitnessStats(
    aggregation,
    groupBy,
    activityType,
    userFirstDay,
    timeframe,
    startDate,
    endDate,
    metrics,
    standardizedUnits = false,
    pendingRequestsKeys
) {
    return {
        type: FETCH_FITNESSSTATS_ACTIVITY,
        payload: {
            aggregation,
            groupBy,
            activityType,
            userFirstDay,
            timeframe,
            startDate,
            endDate,
            metrics,
            standardizedUnits,
            pendingRequestsKeys
        }
    };
}

export const fetchRecentFitnessStats = ({ activityType, metric }) => {
    return {
        type: FETCH_RECENT_FITNESSSTATS_ACTIVITY,
        payload: {
            activityType,
            metric
        }
    };
};

export const fetchUserAveragePace = (activityType) => {
    return {
        type: FETCH_USER_AVERAGE_PACE,
        payload: { activityType }
    };
};

export const fetchAllFitnessStats = (
    { startDate, endDate, key, activityType, metrics, standardizedUnits = false, excludeSort = false },
    evictCache
) => {
    return {
        type: FETCH_ALL_FITNESSSTATS_ACTIVITY,
        payload: { startDate, endDate, key, activityType, metrics, standardizedUnits, excludeSort },
        meta: {
            cacheTime: evictCache ? null : 60000
        }
    };
};

/**
 * If pendingRequestsKeys are provided, they keep track of which calls are being processed.
 * This is to prevent flicker if this call is called multiple times for the same page when one
 * requests finishes before another (example used in Gaming Win Rate Report).
 */
const fetchFitnessStatsFetching = (state, action) => {
    const pendingRequests = [...state.fitnessStatsFetchingPendingRequests];

    if (action.pendingRequestsKeys) {
        pendingRequests.push(action.pendingRequestsKeys);
    }

    return {
        ...state,
        fitnessStatsFetchSuccess: false,
        fitnessStatsFetchError: false,
        fitnessStatsFetching: true,
        fitnessStatsFetchingPendingRequests: pendingRequests
    };
};

const fetchFitnessStatsError = (state, action) => {
    return {
        ...state,
        fitnessStatsFetchSuccess: false,
        fitnessStatsFetchError: true,
        fitnessStatsFetching: false,
        fitnessStatsFetchingPendingRequests: []
    };
};

const updateFitnessStats = (state, action) => {
    let activityType = getActivityType(action.activityType, action.groupBy);

    let activityMetrics = state[action.aggregation] ? state[action.aggregation][activityType] : {};
    if (!activityMetrics) {
        activityMetrics = {};
    }
    activityMetrics[action.timeframe] = action.stats;

    const pendingRequests = [...state.fitnessStatsFetchingPendingRequests];

    if (action.pendingRequestsKeys) {
        const index = pendingRequests.indexOf(action.pendingRequestsKeys);
        if (index > -1) {
            pendingRequests.splice(index, 1);
        }
    }

    return {
        ...state,
        fitnessStatsFetching: pendingRequests.length > 0 ? true : false,
        fitnessStatsFetchSuccess: pendingRequests.length === 0 ? true : false,
        fitnessStatsFetchError: false,
        fitnessStatsFetchingPendingRequests: pendingRequests,
        [action.aggregation]: {
            ...state[action.aggregation],
            [activityType]: activityMetrics
        }
    };
};

const getActivityType = (activityType, groupBy) => {
    let type = activityType || 'all';
    if (type === 'all' && groupBy === 'ACTIVITY') {
        type = 'groupByActivity';
    } else if (type === 'all' && groupBy === 'EVENT') {
        type = 'groupByEvent';
    }
    return type;
};

const fetchRecentFitnessStatsSuccess = (state, action) => {
    return {
        ...state,
        fitnessRecentStats: {
            ...state.fitnessRecentStats,
            metrics: {
                ...state.fitnessRecentStats.metrics,
                [action.metric]: action.stats
            },
            fitnessRecentStatsFetching: {
                ...state.fitnessRecentStats.fitnessRecentStatsFetching,
                [action.metric]: false
            },
            fitnessRecentStatsFetchError: {
                ...state.fitnessRecentStats.fitnessRecentStatsFetchError,
                [action.metric]: false
            },
            fitnessRecentStatsFetchSuccess: {
                ...state.fitnessRecentStats.fitnessRecentStatsFetchSuccess,
                [action.metric]: true
            }
        }
    };
};

const fetchRecentFitnessStatsFetching = (state, action) => {
    return {
        ...state,
        fitnessRecentStats: {
            ...state.fitnessRecentStats,
            metrics: {
                ...state.fitnessRecentStats.metrics,
                [action.metric]: {}
            },
            fitnessRecentStatsFetching: {
                ...state.fitnessRecentStats.fitnessRecentStatsFetching,
                [action.metric]: true
            },
            fitnessRecentStatsFetchError: {
                ...state.fitnessRecentStats.fitnessRecentStatsFetchError,
                [action.metric]: false
            },
            fitnessRecentStatsFetchSuccess: {
                ...state.fitnessRecentStats.fitnessRecentStatsFetchSuccess,
                [action.metric]: false
            }
        }
    };
};

const fetchRecentFitnessStatsError = (state, action) => {
    return {
        ...state,
        fitnessRecentStats: {
            ...state.fitnessRecentStats,
            metrics: {
                ...state.fitnessRecentStats.metrics,
                [action.metric]: {}
            },
            fitnessRecentStatsFetching: {
                ...state.fitnessRecentStats.fitnessRecentStatsFetching,
                [action.metric]: false
            },
            fitnessRecentStatsFetchError: {
                ...state.fitnessRecentStats.fitnessRecentStatsFetchError,
                [action.metric]: true
            },
            fitnessRecentStatsFetchSuccess: {
                ...state.fitnessRecentStats.fitnessRecentStatsFetchSuccess,
                [action.metric]: false
            }
        }
    };
};

const fetchUserAveragePaceSuccess = (state, action) => {
    return {
        ...state,
        userAveragePace: {
            userAvgPace: {
                ...state.userAveragePace.userAvgPace,
                [action.activityType]: action.userAveragePace
            },
            userAvgPaceFetching: false,
            userAvgPaceFetchSuccess: true,
            userAvgPaceFetchError: false
        }
    };
};

const fetchUserAveragePaceFetching = (state, action) => {
    return {
        ...state,
        userAveragePace: {
            ...state.userAveragePace,
            userAvgPaceFetching: true,
            userAvgPaceFetchSuccess: false,
            userAvgPaceFetchError: false
        }
    };
};

const fetchUserAveragePaceError = (state, action) => {
    return {
        ...state,
        userAveragePace: {
            ...state.userAveragePace,
            userAvgPaceFetching: false,
            userAvgPaceFetchSuccess: false,
            userAvgPaceFetchError: true
        }
    };
};

const fetchAllFitnessStatsSuccess = (state, action) => {
    let newAllFitnessStats = [];
    const existingRangeIndex = state.fitnessAllStats.findIndex((range) => {
        return range.startDate === action.startDate && range.endDate === action.endDate;
    });

    if (existingRangeIndex >= 0) {
        // if exiting date range exists, update the range with new data
        // toSpliced returns a copy instead of modifying in place
        newAllFitnessStats = state.fitnessAllStats.toSpliced(existingRangeIndex, 1, {
            startDate: action.startDate,
            endDate: action.endDate,
            key: action.key ? action.key : null,
            data: action.allFitnessStats,
            allStatsFetching: false,
            allStatsError: false,
            allStatsSuccess: true
        });
    } else {
        newAllFitnessStats = [...state.fitnessAllStats];
        newAllFitnessStats.push({
            startDate: action.startDate,
            endDate: action.endDate,
            key: action.key ? action.key : null,
            data: action.allFitnessStats,
            allStatsFetching: false,
            allStatsError: false,
            allStatsSuccess: true
        });
    }

    return {
        ...state,
        fitnessAllStats: newAllFitnessStats
    };
};

const fetchAllFitnessStatsFetching = (state, action) => {
    let newAllFitnessStats = [];
    const existingRangeIndex = state.fitnessAllStats.findIndex((range) => {
        return range.startDate === action.startDate && range.endDate === action.endDate;
    });

    if (existingRangeIndex >= 0) {
        // if exiting date range exists, update the range with new data
        // toSpliced returns a copy instead of modifying in place
        newAllFitnessStats = state.fitnessAllStats.toSpliced(existingRangeIndex, 1, {
            startDate: action.startDate,
            endDate: action.endDate,
            key: action.key ? action.key : null,
            data: [],
            allStatsFetching: true,
            allStatsError: false,
            allStatsSuccess: false
        });
    } else {
        newAllFitnessStats = [...state.fitnessAllStats];
        newAllFitnessStats.push({
            startDate: action.startDate,
            endDate: action.endDate,
            key: action.key ? action.key : null,
            data: [],
            allStatsFetching: true,
            allStatsError: false,
            allStatsSuccess: false
        });
    }

    return {
        ...state,
        fitnessAllStats: newAllFitnessStats
    };
};

const fetchAllFitnessStatsError = (state, action) => {
    let newAllFitnessStats = [];
    const existingRangeIndex = state.fitnessAllStats.findIndex((range) => {
        return range.startDate === action.startDate && range.endDate === action.endDate;
    });

    if (existingRangeIndex >= 0) {
        // if exiting date range exists, update the range with new data
        // toSpliced returns a copy instead of modifying in place
        newAllFitnessStats = state.fitnessAllStats.toSpliced(existingRangeIndex, 1, {
            startDate: action.startDate,
            endDate: action.endDate,
            key: action.key ? action.key : null,
            data: [],
            allStatsFetching: false,
            allStatsError: true,
            allStatsSuccess: false
        });
    } else {
        newAllFitnessStats = [...state.fitnessAllStats];
        newAllFitnessStats.push({
            startDate: action.startDate,
            endDate: action.endDate,
            key: action.key ? action.key : null,
            data: [],
            allStatsFetching: false,
            allStatsError: true,
            allStatsSuccess: false
        });
    }

    return {
        ...state,
        fitnessAllStats: newAllFitnessStats
    };
};

const handlers = {
    [FETCH_FITNESSSTATS_ACTIVITY_SUCCESS]: updateFitnessStats,
    [FETCH_FITNESSSTATS_ACTIVITY_FETCHING]: fetchFitnessStatsFetching,
    [FETCH_FITNESSSTATS_ACTIVITY_ERROR]: fetchFitnessStatsError,
    [FETCH_RECENT_FITNESSSTATS_ACTIVITY_SUCCESS]: fetchRecentFitnessStatsSuccess,
    [FETCH_RECENT_FITNESSSTATS_ACTIVITY_FETCHING]: fetchRecentFitnessStatsFetching,
    [FETCH_RECENT_FITNESSSTATS_ACTIVITY_ERROR]: fetchRecentFitnessStatsError,
    [FETCH_USER_AVERAGE_PACE_SUCCESS]: fetchUserAveragePaceSuccess,
    [FETCH_USER_AVERAGE_PACE_FETCHING]: fetchUserAveragePaceFetching,
    [FETCH_USER_AVERAGE_PACE_ERROR]: fetchUserAveragePaceError,
    [FETCH_ALL_FITNESSSTATS_ACTIVITY_SUCCESS]: fetchAllFitnessStatsSuccess,
    [FETCH_ALL_FITNESSSTATS_ACTIVITY_FETCHING]: fetchAllFitnessStatsFetching,
    [FETCH_ALL_FITNESSSTATS_ACTIVITY_ERROR]: fetchAllFitnessStatsError
};

export default (state = INITIAL_STATE, action = {}) => {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
};

export const isRecentFitnessError = (state, metric) => {
    return state.fitnessStats.fitnessRecentStats?.fitnessRecentStatsFetchError[metric];
};

export const isRecentFitnessFetching = (state, metric) => {
    return state.fitnessStats.fitnessRecentStats?.fitnessRecentStatsFetching[metric];
};

export const getRecentFitnessStats = (state, metric) => {
    return state.fitnessStats.fitnessRecentStats?.metrics[metric];
};

export const isUserAveragePaceFetching = (state) => {
    return state.fitnessStats.userAveragePace.userAvgPaceFetching;
};

export const getUserAveragePace = (state) => {
    return state.fitnessStats.userAveragePace.userAvgPace;
};

export const isAllFitnessFetching = (state, startDate, endDate, key = null) => {
    let allStats = state.fitnessStats.fitnessAllStats;

    if (key) {
        allStats = allStats.filter((stat) => {
            return stat.key === key;
        });
    }

    const range = allStats?.find((stats) => {
        return stats.startDate === startDate && stats.endDate === endDate;
    });
    return range?.allStatsFetching;
};

export const isAllFitnessError = (state, startDate, endDate, key = null) => {
    let allStats = state.fitnessStats.fitnessAllStats;

    if (key) {
        allStats = allStats.filter((stat) => {
            return stat.key === key;
        });
    }

    const range = allStats?.find((stats) => {
        return stats.startDate === startDate && stats.endDate === endDate;
    });
    return range?.allStatsError;
};

export const isAllFitnessSuccess = (state, startDate, endDate, key = null) => {
    let allStats = state.fitnessStats.fitnessAllStats;

    if (key) {
        allStats = allStats.filter((stat) => {
            return stat.key === key;
        });
    }

    const range = allStats?.find((stats) => {
        return stats.startDate === startDate && stats.endDate === endDate;
    });
    return range?.allStatsSuccess;
};

export const getAllFitnessStatsSpecificRange = (state, startDate, endDate, filterFn, key = null) => {
    let allStats = state.fitnessStats.fitnessAllStats;

    if (key) {
        allStats = allStats.filter((stat) => {
            return stat.key === key;
        });
    }
    const range = allStats?.find((stats) => {
        return stats.startDate === startDate && stats.endDate === endDate;
    });

    if (typeof filterFn === 'function') {
        return filterFn(range?.data);
    }
    return range?.data;
};

export const getAllFitnessStatsAllRanges = (state, filterFn, key = null) => {
    let allStats = state.fitnessStats.fitnessAllStats;

    if (key) {
        allStats = allStats.filter((stat) => {
            return stat.key === key;
        });
    }
    return allStats?.map((stats) => {
        return { ...stats, data: typeof filterFn === 'function' ? filterFn(stats.data) : stats.data };
    });
};

export const isAllFitnessAllRangesFetching = (state, key = null) => {
    let allStats = state.fitnessStats.fitnessAllStats;

    if (key) {
        allStats = allStats.filter((stat) => {
            return stat.key === key;
        });
    }

    return allStats?.some((stats) => {
        return stats.allStatsFetching;
    });
};
